import { Box, useTheme } from "@mui/material";
import { CurrencyCode } from "@smartrr/shared/shopifyGraphQL/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { AquariusSelect } from "@customer-app/account/views/OperationAquarius/components/AquariusSelect";
import { createVariantSelectorOptions } from "@customer-app/utils/createVariantSelectorOptions";

import { IVariantSelectorProps } from "../types";

export const VariantSelector = ({
  purchasable,
  currency,
  isLoading,
  item,
  setSelectedVariantOption,
  selectedVariantOption,
  enabledVariantOptions,
  includeAddons = false,
  includeTrending = false,
  ...props
}: IVariantSelectorProps): JSX.Element => {
  const theme = useTheme();
  const hasRegularSellingPlans = selectedVariantOption ? !!selectedVariantOption.sellingPlan : false;
  const isAvailableAsAddOn = selectedVariantOption ? !!selectedVariantOption.addonConfig : false;
  const showVariant =
    theme.modernThemeSettings?.subscriptionActions.showVariantName &&
    theme.modernThemeSettings?.showHide.showVariantName;
  const onlyAvailableAsAddOn = !hasRegularSellingPlans && isAvailableAsAddOn;
  const [subscriptionIsSelected, setIsSubscriptionIsSelected] = useState(!onlyAvailableAsAddOn);

  const variantSelectorOptions = useMemo(
    () =>
      enabledVariantOptions.length
        ? createVariantSelectorOptions(
            enabledVariantOptions,
            currency as CurrencyCode,
            includeAddons,
            !subscriptionIsSelected,
            true,
            includeTrending,
            includeTrending
          )
        : [],
    [enabledVariantOptions, subscriptionIsSelected, currency]
  );

  const onVariantChange = useCallback(
    (nextVariantId: string) => {
      // if new selection no longer has selected option, update

      const nextVariantOption = enabledVariantOptions.find(
        variantOption => variantOption.vnt.id === nextVariantId
      )!;
      if (subscriptionIsSelected && !nextVariantOption.sellingPlan) {
        setIsSubscriptionIsSelected(false);
      } else if (!subscriptionIsSelected && !nextVariantOption.addonConfig) {
        setIsSubscriptionIsSelected(true);
      }
      setSelectedVariantOption && setSelectedVariantOption(nextVariantOption);
    },
    [subscriptionIsSelected, enabledVariantOptions]
  );

  // if no variant is active (on land), setting selected variant to be first available variant from enabledVariantOptions
  useEffect(() => {
    if (!selectedVariantOption || !purchasable.vnts?.find(vnt => vnt.id === selectedVariantOption.vnt.id)) {
      setSelectedVariantOption(
        enabledVariantOptions.find(enabledVariantOption =>
          purchasable?.vnts?.some(availableVnt => enabledVariantOption.vnt.id === availableVnt.id)
        )!
      );
    }
  }, [purchasable.id, selectedVariantOption?.vnt.id]);

  return (
    <Box {...props}>
      {/* If variant is shown and only 1 variant exists then variant selector isn't needed */}
      {variantSelectorOptions.length > (showVariant ? 1 : 0) ? (
        <Box sx={{ width: "100%", marginTop: "15px" }}>
          <AquariusSelect
            data-testid="variant-selector"
            options={variantSelectorOptions}
            onChange={onVariantChange}
            value={selectedVariantOption ? selectedVariantOption?.vnt.id : enabledVariantOptions[0].vnt.id}
            placeholder="Select"
            disabled={isLoading}
          />
        </Box>
      ) : null}
    </Box>
  );
};
